<!-- BulkDemandNoticeComponentFixed.vue -->
<template>
  <div>
    <!-- Main Modal -->
    <div class="modal fade" id="bulkDemandNoticeModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header text-white">
            <h5 class="modal-title">
              <i class="mdi mdi-email-multiple me-2"></i>
              Bulk Send Demand Notices
            </h5>
            <button type="button" class="btn-close btn-close-black" @click="closeModal"></button>
          </div>

          <div class="modal-body">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted">Loading properties data...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger">
              <h6><i class="mdi mdi-alert-circle me-2"></i>Error Loading Data</h6>
              <p class="mb-0">{{ error }}</p>
              <button @click="retryLoad" class="btn btn-sm btn-danger mt-2">
                <i class="mdi mdi-refresh me-1"></i>Retry
              </button>
            </div>

            <!-- No Data State -->
            <div v-else-if="!loading && properties.length === 0" class="text-center py-5">
              <i class="mdi mdi-database-off-outline" style="font-size: 48px; color: #6c757d;"></i>
              <h5 class="mt-3 text-muted">No Properties Found</h5>
              <p class="text-muted">No properties with outstanding balances and contact information were found.</p>
              <button @click="refreshData" class="btn btn-outline-primary">
                <i class="mdi mdi-refresh me-1"></i>Refresh
              </button>
            </div>

            <!-- Main Content -->
            <div v-else>
              <!-- Statistics Cards -->
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body bg-primary bg-gradient text-white rounded">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <p class="mb-1 text-white-50">Total Properties</p>
                          <h3 class="mb-0" style="color: white">{{ filteredProperties.length }}</h3>
                        </div>
                        <i class="mdi mdi-home-group" style="font-size: 32px; opacity: 0.3;"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body bg-success bg-gradient text-white rounded">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <p class="mb-1 text-white-50">Selected</p>
                          <h3 class="mb-0" style="color: white">{{ selectedProperties.length }}</h3>
                        </div>
                        <i class="mdi mdi-check-circle" style="font-size: 32px; opacity: 0.3;"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body bg-info bg-gradient text-white rounded">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <p class="mb-1 text-white-50">With Contact</p>
                          <h3 class="mb-0">{{ propertiesWithContact }}</h3>
                        </div>
                        <i class="mdi mdi-email-check" style="font-size: 32px; opacity: 0.3;"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body bg-warning bg-gradient text-white rounded">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <p class="mb-1 text-white-50">Total Due</p>
                          <h3 class="mb-0" style="color: white">{{ formatCurrency(totalAmountDue) }}</h3>
                        </div>
                        <i class="mdi mdi-currency-usd" style="font-size: 32px; opacity: 0.3;"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Filters Section -->
              <div class="card mb-3 border-0 shadow-sm">
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label small text-muted">Search</label>
                      <div class="input-group">
                        <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                        <input
                            v-model="searchQuery"
                            type="text"
                            class="form-control"
                            placeholder="Search by LR Number, Owner Name..."
                            @input="filterProperties"
                        >
                      </div>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small text-muted">Min Amount (KES)</label>
                      <input
                          v-model.number="filters.minAmount"
                          type="number"
                          class="form-control"
                          placeholder="0"
                          @input="filterProperties"
                      >
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small text-muted">Sub County</label>
                      <select v-model="filters.subCounty" class="form-select" @change="filterProperties">
                        <option value="">All Sub Counties</option>
                        <option v-for="subCounty in uniqueSubCounties" :key="subCounty" :value="subCounty">
                          {{ subCounty }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label small text-muted">&nbsp;</label>
                      <button @click="clearFilters" class="btn btn-outline-secondary w-100">
                        <i class="mdi mdi-filter-remove me-1"></i>Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="form-check">
                  <input
                      v-model="selectAll"
                      type="checkbox"
                      class="form-check-input"
                      id="selectAllCheck"
                      @change="toggleSelectAll"
                  >
                  <label class="form-check-label" for="selectAllCheck">
                    Select All ({{ filteredProperties.length }} properties)
                  </label>
                </div>
                <button
                    @click="openSendConfirmation"
                    :disabled="selectedProperties.length === 0"
                    class="btn btn-primary"
                >
                  <i class="mdi mdi-send me-2"></i>
                  Send to {{ selectedProperties.length }} Properties
                </button>
              </div>

              <!-- Properties Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="table-light">
                  <tr>
                    <th width="50"></th>
                    <th>LR Number</th>
                    <th>Owner Name</th>
                    <th>Sub County</th>
                    <th>Ward</th>
                    <th class="text-end">Current Balance</th>
                    <th class="text-end">Arrears</th>
                    <th class="text-center">Contact</th>
                    <th>Last Notice</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="property in paginatedProperties" :key="property.id">
                    <td>
                      <input
                          type="checkbox"
                          :value="property.id"
                          v-model="selectedPropertyIds"
                          @change="updateSelectedProperties"
                      >
                    </td>
                    <td>
                      <strong>{{ property.LRNumber }}</strong>
                    </td>
                    <td>{{ property.ownerNames }}</td>
                    <td>{{ property.subCountyName || '-' }}</td>
                    <td>{{ property.wardName || '-' }}</td>
                    <td class="text-end">
                        <span :class="getAmountClass(property.currentBalance)">
                          {{ formatCurrency(property.currentBalance) }}
                        </span>
                    </td>
                    <td class="text-end">
                      {{ formatCurrency(property.outstandingArrears) }}
                    </td>
                    <td class="text-center">
                        <span v-if="property.ownerEmail" class="badge bg-success me-1" :title="property.ownerEmail">
                          <i class="mdi mdi-email"></i>
                        </span>
                      <span v-if="property.ownerPhoneNo" class="badge bg-info" :title="property.ownerPhoneNo">
                          <i class="mdi mdi-phone"></i>
                        </span>
                      <span v-if="!property.ownerEmail && !property.ownerPhoneNo" class="text-muted">
                          -
                        </span>
                    </td>
                    <td>
                        <span v-if="property.lastNoticeDate" class="text-muted small">
                          {{ formatDate(property.lastNoticeDate) }}
                        </span>
                      <span v-else class="badge bg-warning text-dark">Never sent</span>
                    </td>
                  </tr>
                  <tr v-if="paginatedProperties.length === 0">
                    <td colspan="9" class="text-center text-muted py-4">
                      No properties match your filters
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div class="d-flex justify-content-between align-items-center mt-3" v-if="filteredProperties.length > 0">
                <div class="text-muted">
                  Showing {{ paginationInfo.start }} to {{ paginationInfo.end }} of {{ filteredProperties.length }} entries
                </div>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="javascript: void(0);" @click.prevent="changePage(1)" v-if="currentPage > 2">First</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="javascript: void(0);" @click.prevent="changePage(currentPage - 1)">Previous</a>
                    </li>
                    <li
                        v-for="page in visiblePages"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === currentPage }"
                    >
                      <a class="page-link" href="javascript: void(0);" @click.prevent="changePage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="javascript: void(0);" @click.prevent="changePage(currentPage + 1)">Next</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="javascript: void(0);" @click.prevent="changePage(totalPages)" v-if="currentPage < totalPages - 1">Last</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Send Confirmation Modal -->
    <div class="modal fade" id="sendConfirmModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Bulk Send</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Summary -->
            <div class="alert alert-info" v-if="!sending && !sendComplete">
              <h6 class="alert-heading"><i class="mdi mdi-information me-2"></i>Send Summary</h6>
              <div class="row mt-3">
                <div class="col-md-6">
                  <p class="mb-1"><strong>Total Properties:</strong> {{ selectedProperties.length }}</p>
                  <p class="mb-1"><strong>With Email:</strong> {{ selectedWithEmail.length }}</p>
                  <p class="mb-0"><strong>With Phone:</strong> {{ selectedWithPhone.length }}</p>
                </div>
                <div class="col-md-6">
                  <p class="mb-1"><strong>Total Amount Due:</strong> {{ formatCurrency(totalAmountDue) }}</p>
                  <p class="mb-1"><strong>Total Arrears:</strong> {{ formatCurrency(totalArrears) }}</p>
                  <p class="mb-0"><strong>No Contact Info:</strong> {{ selectedWithoutContact.length }}</p>
                </div>
              </div>
            </div>

            <!-- Send Options -->
            <div class="row mb-3" v-if="!sending && !sendComplete">
              <div class="col-md-6">
                <div class="form-check">
                  <input v-model="sendOptions.email" type="checkbox" class="form-check-input" id="optEmail">
                  <label class="form-check-label" for="optEmail">
                    <strong>Send Email Notices</strong>
                    <small class="d-block text-muted">To {{ selectedWithEmail.length }} properties</small>
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check">
                  <input v-model="sendOptions.sms" type="checkbox" class="form-check-input" id="optSMS">
                  <label class="form-check-label" for="optSMS">
                    <strong>Send SMS Notifications</strong>
                    <small class="d-block text-muted">To {{ selectedWithPhone.length }} properties</small>
                  </label>
                </div>
              </div>
            </div>

            <!-- Custom Message -->
            <div class="mb-3" v-if="!sending && !sendComplete">
              <label class="form-label">Additional Message (Optional)</label>
              <textarea
                  v-model="sendOptions.customMessage"
                  class="form-control"
                  rows="3"
                  placeholder="Enter any additional message to include..."
              ></textarea>
            </div>

            <!-- Progress (shown during sending) -->
            <div v-if="sending" class="text-center">
              <div class="mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Sending...</span>
                </div>
              </div>
              <h5>Sending Demand Notices...</h5>
              <div class="progress mb-2" style="height: 25px;">
                <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    :style="{ width: sendProgress + '%' }"
                >
                  {{ sendProgress }}%
                </div>
              </div>
              <p class="text-muted small mb-0">
                Processing {{ processedCount }} of {{ selectedProperties.length }} properties...
              </p>
            </div>

            <!-- Results (shown after sending) -->
            <div v-if="sendComplete" class="text-center">
              <div class="mb-4">
                <i class="mdi mdi-check-circle text-success" style="font-size: 48px;"></i>
                <h4 class="text-success mt-2">Send Complete!</h4>
              </div>
              <div class="row">
                <div class="col-md-4">
                  <div class="card border-success">
                    <div class="card-body">
                      <h3 class="text-success">{{ sendResults.success }}</h3>
                      <p class="mb-0">Successful</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card border-danger">
                    <div class="card-body">
                      <h3 class="text-danger">{{ sendResults.failed }}</h3>
                      <p class="mb-0">Failed</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card border-warning">
                    <div class="card-body">
                      <h3 class="text-warning">{{ sendResults.skipped }}</h3>
                      <p class="mb-0">Skipped</p>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="sendResults.errors.length > 0" class="alert alert-danger mt-3 text-start">
                <h6>Errors:</h6>
                <ul class="mb-0">
                  <li v-for="(error, index) in sendResults.errors.slice(0, 5)" :key="index">
                    {{ error }}
                  </li>
                </ul>
                <small v-if="sendResults.errors.length > 5" class="text-muted">
                  And {{ sendResults.errors.length - 5 }} more errors...
                </small>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ sendComplete ? 'Close' : 'Cancel' }}
            </button>
            <button
                v-if="!sending && !sendComplete"
                @click="processBulkSend"
                type="button"
                class="btn btn-primary"
                :disabled="!sendOptions.email && !sendOptions.sms"
            >
              <i class="mdi mdi-send me-2"></i>Send Notices
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, landRate } from "@/api";

export default {
  name: 'BulkDemandNoticeComponentFixed',
  data() {
    return {
      // Modal instance
      modalInstance: null,

      // Loading states
      loading: false,
      error: null,
      dataLoaded: false,

      // Properties data
      properties: [],
      filteredProperties: [],
      selectedPropertyIds: [],
      selectedProperties: [],

      // Filters
      searchQuery: '',
      filters: {
        minAmount: null,
        subCounty: ''
      },
      selectAll: false,

      // Pagination
      currentPage: 1,
      itemsPerPage: 10,

      // Send options
      sendOptions: {
        email: true,
        sms: true,
        customMessage: ''
      },

      // Send progress
      sending: false,
      sendComplete: false,
      processedCount: 0,
      sendResults: {
        success: 0,
        failed: 0,
        skipped: 0,
        errors: []
      }
    };
  },
  computed: {
    uniqueSubCounties() {
      const counties = [...new Set(this.properties.map(p => p.subCountyName).filter(Boolean))];
      return counties.sort();
    },

    propertiesWithContact() {
      return this.filteredProperties.filter(p => p.ownerEmail || p.ownerPhoneNo).length;
    },

    selectedWithEmail() {
      return this.selectedProperties.filter(p => p.ownerEmail);
    },

    selectedWithPhone() {
      return this.selectedProperties.filter(p => p.ownerPhoneNo);
    },

    selectedWithoutContact() {
      return this.selectedProperties.filter(p => !p.ownerEmail && !p.ownerPhoneNo);
    },

    totalAmountDue() {
      return this.selectedProperties.reduce((sum, p) => sum + (parseFloat(p.currentBalance) || 0), 0);
    },

    totalArrears() {
      return this.selectedProperties.reduce((sum, p) => sum + (parseFloat(p.outstandingArrears) || 0), 0);
    },

    paginatedProperties() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProperties.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredProperties.length / this.itemsPerPage);
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    paginationInfo() {
      const start = this.filteredProperties.length === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredProperties.length);
      return { start, end };
    },

    sendProgress() {
      if (this.selectedProperties.length === 0) return 0;
      return Math.round((this.processedCount / this.selectedProperties.length) * 100);
    }
  },
  methods: {
    async openModal() {
      // Reset state
      this.resetState();

      // Show modal first
      this.$nextTick(() => {
        const modalElement = document.getElementById('bulkDemandNoticeModal');
        if (modalElement) {
          this.modalInstance = new bootstrap.Modal(modalElement);
          this.modalInstance.show();

          // Load data after modal is shown
          setTimeout(() => {
            this.loadProperties();
          }, 100);
        }
      });
    },

    closeModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
    },

    resetState() {
      this.loading = false;
      this.error = null;
      this.dataLoaded = false;
      this.properties = [];
      this.filteredProperties = [];
      this.selectedPropertyIds = [];
      this.selectedProperties = [];
      this.searchQuery = '';
      this.filters = {
        minAmount: null,
        subCounty: ''
      };
      this.selectAll = false;
      this.currentPage = 1;
    },

    async loadProperties() {
      this.loading = true;
      this.error = null;
      this.dataLoaded = false;

      try {
        const data = new FormData();
        data.append("function", "getPropertiesForBulkNotice");

        console.log('Loading properties...');
        const response = await execute(data, landRate);
        console.log('API Response:', response);

        if (response?.data?.success && response?.data?.data?.properties) {
          const props = response.data.data.properties;
          this.properties = Array.isArray(props) ? props : [];
          this.filteredProperties = [...this.properties];
          this.dataLoaded = true;
          console.log(`Successfully loaded ${this.properties.length} properties`);
        } else {
          throw new Error(response?.data?.message || 'Failed to load properties');
        }
      } catch (err) {
        console.error('Error loading properties:', err);
        this.error = err.message || 'Failed to load properties. Please try again.';
        this.properties = [];
        this.filteredProperties = [];
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.loadProperties();
    },

    async retryLoad() {
      this.error = null;
      await this.loadProperties();
    },

    filterProperties() {
      let filtered = [...this.properties];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(p =>
            (p.LRNumber && p.LRNumber.toLowerCase().includes(query)) ||
            (p.ownerNames && p.ownerNames.toLowerCase().includes(query)) ||
            (p.subCountyName && p.subCountyName.toLowerCase().includes(query)) ||
            (p.wardName && p.wardName.toLowerCase().includes(query))
        );
      }

      // Amount filter
      if (this.filters.minAmount !== null && this.filters.minAmount !== '' && this.filters.minAmount >= 0) {
        filtered = filtered.filter(p => (parseFloat(p.currentBalance) || 0) >= this.filters.minAmount);
      }

      // Sub County filter
      if (this.filters.subCounty) {
        filtered = filtered.filter(p => p.subCountyName === this.filters.subCounty);
      }

      this.filteredProperties = filtered;
      this.currentPage = 1;
      this.updateSelectedAfterFilter();
    },

    clearFilters() {
      this.searchQuery = '';
      this.filters.minAmount = null;
      this.filters.subCounty = '';
      this.filterProperties();
    },

    toggleSelectAll() {
      if (this.selectAll) {
        // Select all filtered properties
        this.selectedPropertyIds = this.filteredProperties.map(p => p.id);
      } else {
        // Deselect all
        this.selectedPropertyIds = [];
      }
      this.updateSelectedProperties();
    },

    updateSelectedProperties() {
      this.selectedProperties = this.properties.filter(p =>
          this.selectedPropertyIds.includes(p.id)
      );

      // Update selectAll checkbox state
      this.selectAll = this.filteredProperties.length > 0 &&
          this.filteredProperties.every(p => this.selectedPropertyIds.includes(p.id));
    },

    updateSelectedAfterFilter() {
      // Remove selected items that are no longer in filtered list
      const filteredIds = this.filteredProperties.map(p => p.id);
      this.selectedPropertyIds = this.selectedPropertyIds.filter(id => filteredIds.includes(id));
      this.updateSelectedProperties();
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    openSendConfirmation() {
      this.sendComplete = false;
      this.sending = false;
      this.processedCount = 0;
      this.sendResults = {
        success: 0,
        failed: 0,
        skipped: 0,
        errors: []
      };

      const modal = new bootstrap.Modal(document.getElementById('sendConfirmModal'));
      modal.show();
    },

    async processBulkSend() {
      this.sending = true;
      this.processedCount = 0;

      // Process in batches to avoid overwhelming the server
      const batchSize = 5;

      for (let i = 0; i < this.selectedProperties.length; i += batchSize) {
        const batch = this.selectedProperties.slice(i, i + batchSize);

        await Promise.all(batch.map(async (property) => {
          try {
            // Check if property has contact info
            const hasEmail = property.ownerEmail && this.sendOptions.email;
            const hasPhone = property.ownerPhoneNo && this.sendOptions.sms;

            if (!hasEmail && !hasPhone) {
              this.sendResults.skipped++;
              this.sendResults.errors.push(`${property.LRNumber}: No valid contact method`);
            } else {
              await this.sendSingleNotice(property);
              this.sendResults.success++;
            }
          } catch (error) {
            this.sendResults.failed++;
            this.sendResults.errors.push(`${property.LRNumber}: ${error.message}`);
          }

          this.processedCount++;
        }));

        // Small delay between batches
        if (i + batchSize < this.selectedProperties.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      this.sending = false;
      this.sendComplete = true;
    },

    async sendSingleNotice(property) {
      const data = new FormData();
      data.append("function", "emailDemandNotice");
      data.append("propertyId", property.id);

      if (this.sendOptions.email && property.ownerEmail) {
        data.append("recipientEmail", property.ownerEmail);
      }

      if (this.sendOptions.sms && property.ownerPhoneNo) {
        data.append("recipientPhone", property.ownerPhoneNo);
      }

      if (this.sendOptions.customMessage) {
        data.append("customMessage", this.sendOptions.customMessage);
      }

      const response = await execute(data, landRate);

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to send notice');
      }

      return response.data;
    },

    formatCurrency(amount) {
      const value = parseFloat(amount) || 0;
      return `KES ${new Intl.NumberFormat('en-GB').format(value)}`;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    getAmountClass(amount) {
      const value = parseFloat(amount) || 0;
      if (value >= 50000) return 'text-danger fw-bold';
      if (value >= 10000) return 'text-warning';
      return '';
    }
  },

  beforeUnmount() {
    // Clean up modal instance
    if (this.modalInstance) {
      this.modalInstance.dispose();
    }
  }
};
</script>

<style scoped>
.table th {
  font-weight: 600;
  white-space: nowrap;
  background-color: #f8f9fa;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.progress {
  height: 25px;
}

.progress-bar {
  font-size: 0.875rem;
  line-height: 25px;
}

.modal-xl {
  max-width: 90%;
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.pagination {
  margin-bottom: 0;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.table-responsive {
  max-height: 60vh;
  overflow-y: auto;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}
</style>